<template>
  <div class="w-full min-h-screen flex flex-col">
    <!-- Header with Logo -->
    <HeaderBar :logo="logo">
      <template #account>
        <LoginDropdown />
      </template>
    </HeaderBar>

    <!-- Main Content -->
    <div class="flex-1 w-full">
      <div class="max-w-7xl mx-auto sm:px-6 px-3 sm:py-8 py-4">
        <!-- Loading Card -->
        <section
          v-if="loading"
          class="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-gray-100 p-12 flex flex-col items-center justify-center gap-4"
        >
          <img class="h-24" :src="logo" alt="Loading" />
          <p class="text-gray-600">Loading...</p>
        </section>

        <!-- Error Card -->
        <section
          v-else-if="!loading_ok"
          class="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-gray-100 p-12 text-center"
        >
          <img class="h-16 mx-auto opacity-70" :src="logo" alt="" />
          <p class="mt-4 text-red-600 font-medium">
            {{ error || "An error occurred" }}
          </p>
          <p
            v-if="error?.includes('not found')"
            class="mt-2 text-sm text-gray-600"
          >
            The investigation you're looking for may not be publicly available.
            Please contact sciqc@elettra.eu for assistance.
          </p>
        </section>

        <!-- Content Card -->
        <section
          v-else
          class="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-gray-100"
        >
          <div class="p-4 sm:p-8 lg:p-12 space-y-8">
            <!-- Title -->
            <header class="flex flex-wrap items-start justify-between gap-4">
              <div class="space-y-1">
                <h1
                  class="text-2xl font-semibold flex flex-wrap items-center gap-3"
                >
                  <span>
                    {{ investigation_info?.title || investigation_info?.name }}
                  </span>
                  <span
                    v-if="selectedVersion !== null && !isLatestVersion"
                    class="text-xs font-medium px-2 py-1 rounded-lg bg-periwinkle-100 border border-gray-200"
                  >
                    Version {{ selectedVersion }}
                  </span>
                  <span
                    v-if="isLatestVersion"
                    class="text-xs font-medium px-2 py-1 rounded-lg bg-green-100 text-green-700 border border-green-200"
                  >
                    Latest
                  </span>
                </h1>
                <p class="text-xs text-gray-500">ID: {{ investigationId }}</p>
              </div>

              <!-- Version selector -->
              <div v-if="versions.length > 1" class="text-sm">
                <label for="version-select" class="mr-2">version :</label>
                <select
                  id="version-select"
                  class="border-2 border-gray-200 rounded-xl px-2 py-1 bg-white"
                  :value="selectedVersion"
                  :disabled="file_structure_loading"
                  @change="changeVersion(Number($event.target.value))"
                >
                  <option v-for="v in versions" :key="v" :value="v">
                    {{ v === latestVersion ? "Latest" : `Version ${v}` }}
                  </option>
                </select>
              </div>
            </header>

            <!-- Citation -->
            <section class="text-left space-y-2">
              <div class="flex justify-between items-center">
                <p class="font-semibold">Citation</p>
                <div class="text-sm">
                  <span class="mr-2">style :</span>
                  <select
                    class="border-2 border-gray-200 rounded-xl px-2 py-1 bg-white"
                    id="citation-style-select"
                    @change="createCitation($event.target.value)"
                  >
                    <option value="apa" default>APA</option>
                    <option value="mla">MLA</option>
                    <option value="chicago">Chicago</option>
                    <option value="harvard">Harvard</option>
                    <option value="vancouver">Vancouver</option>
                    <option value="ieee">IEEE</option>
                    <option value="bibtex">Bibtex</option>
                  </select>
                </div>
              </div>

              <div class="relative mt-3">
                <p
                  class="text-sm text-gray-700 leading-relaxed bg-spicy-mustard-100 border border-gray-200 rounded-xl p-4"
                >
                  <span>{{ citation }}</span>
                </p>
                <Icon
                  icon="mdi:content-copy"
                  class="h-5 w-5 absolute top-3 right-3 cursor-pointer z-[999]"
                  title="Copy to clipboard"
                  @click="copyToClipboard"
                />
              </div>
            </section>

            <!-- Principal Investigator and DOI -->
            <div class="grid gap-4 grid-cols-1" :class="{ 'md:grid-cols-2': doi }">
              <section class="text-left space-y-2">
                <p class="font-semibold">Principal Investigator</p>
                <p
                  class="text-sm text-gray-700 leading-relaxed bg-spicy-mustard-100 border border-gray-200 rounded-xl p-4"
                >
                  {{
                    investigation_info?.principal_investigator ||
                    "No principal investigator information available."
                  }}
                </p>
              </section>

              <section v-if="doi" class="text-left space-y-2">
                <p class="font-semibold">DOI</p>
                <p
                  class="text-sm text-gray-700 leading-relaxed bg-spicy-mustard-100 border border-gray-200 rounded-xl p-4 break-all"
                >
                  <a :href="`https://doi.org/${doi}`" target="_blank">
                    {{ `https://doi.org/${doi}` }}
                  </a>
                </p>
              </section>
            </div>

            <div class="grid justify-between gap-4 grid-cols-2 md:grid-cols-3">
              <!-- Creation Date -->
              <section class="text-left space-y-2 grow-1">
                <p class="font-semibold">Submission date</p>
                <p
                  class="text-sm text-gray-700 leading-relaxed bg-spicy-mustard-100 border border-gray-200 rounded-xl p-4"
                >
                  {{
                    investigation_info?.publication_date || "No date available."
                  }}
                </p>
              </section>

              <!-- Size -->
              <section class="text-left space-y-2 grow-1">
                <p class="font-semibold">Total Size</p>
                <p
                  v-if="file_structure_loading"
                  class="text-sm text-gray-400 leading-relaxed bg-spicy-mustard-50 border border-gray-200 rounded-xl p-4 animate-pulse"
                >
                  Loading file data…
                </p>
                <p
                  v-else
                  class="text-sm text-gray-700 leading-relaxed bg-spicy-mustard-100 border border-gray-200 rounded-xl p-4"
                >
                  {{ file_data?.dimension || "—" }}
                </p>
              </section>

              <!-- File types -->
              <section class="text-left space-y-2 grow-1">
                <p class="font-semibold">File types</p>
                <p
                  v-if="file_structure_loading"
                  class="text-sm text-gray-400 leading-relaxed bg-spicy-mustard-50 border border-gray-200 rounded-xl p-4 animate-pulse"
                >
                  Loading file data…
                </p>
                <p
                  v-else
                  class="text-sm text-gray-700 leading-relaxed bg-spicy-mustard-100 border border-gray-200 rounded-xl p-4"
                >
                  {{ fileTypeKeys }}
                </p>
              </section>
            </div>

            <!-- Description -->
            <section class="text-left space-y-2">
              <p class="font-semibold">Description</p>
              <p
                class="text-sm text-gray-700 leading-relaxed bg-spicy-mustard-100 border border-gray-200 rounded-xl p-4"
              >
                <span
                  v-if="selectedVersion !== 0 && file_structure_loading"
                  class="text-gray-400 animate-pulse"
                >
                  Loading description…
                </span>
                <span v-else>{{ descriptionText }}</span>
              </p>
            </section>

            <!-- File Structure -->
            <section>
              <div class="flex items-center justify-between mb-3">
                <h3 class="font-semibold">File Structure</h3>
                <button
                  v-if="!file_data?.file_structure && !file_structure_loading"
                  @click="loadFileStructure"
                  class="group relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Refresh file structure"
                >
                  <Icon icon="mdi:refresh" class="h-5 w-5" />
                  <span
                    class="absolute right-0 top-full mt-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10"
                  >
                    Refresh: data may still be processing
                  </span>
                </button>
              </div>

              <!-- Loading state - skeleton -->
              <div
                v-if="file_structure_loading"
                class="space-y-3 animate-pulse"
              >
                <div class="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div class="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div class="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div class="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div class="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div class="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div class="h-3 bg-gray-200 rounded w-2/3 mb-2"></div>
                  <div class="h-3 bg-gray-200 rounded w-1/3"></div>
                </div>
                <div class="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div class="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
                  <div class="h-3 bg-gray-200 rounded w-full mb-2"></div>
                  <div class="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>

              <!-- Error state -->
              <div
                v-else-if="file_structure_error"
                class="bg-red-50 border border-red-200 rounded-xl p-4"
              >
                <div class="flex items-start gap-3">
                  <Icon
                    icon="mdi:alert-circle-outline"
                    class="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5"
                  />
                  <div class="flex-1">
                    <h4 class="font-semibold text-red-900">
                      Failed to load file structure
                    </h4>
                    <p class="text-sm text-red-800">
                      {{ file_structure_error }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Files not available warning -->
              <div
                v-else-if="file_data?.files_available === false"
                class="bg-amber-50 border border-amber-200 rounded-xl p-4"
              >
                <div class="flex items-start gap-3">
                  <Icon
                    icon="mdi:alert-circle-outline"
                    class="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5"
                  />
                  <div class="flex-1">
                    <h4 class="font-semibold text-amber-900">
                      Data Files Not Available
                    </h4>
                    <p class="text-sm text-amber-800">
                      {{
                        file_data?.files_warning ||
                        "Data files are temporarily not available. Please contact sciqc@elettra.eu to retrieve the data."
                      }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Success state - show file structure -->
              <FileStructureComponent
                v-else-if="file_data?.file_structure"
                class="w-full"
                :title="investigation_info?.title || investigation_info?.name"
                :structure_dict="file_data.file_structure"
                :investigation-id="investigationId"
                :investigation-name="investigation_info?.name || ''"
                @file-open="previewFile = $event"
              />

              <!-- No file data -->
              <p v-else class="text-sm text-gray-500 italic">
                No file structure available.
              </p>
            </section>

            <!-- Actions -->
            <section
              v-if="file_data?.files_available !== false || (isOwner && file_data?.versionable)"
              class="flex flex-wrap justify-end gap-3"
            >
              <button
                v-if="isOwner && file_data?.versionable"
                class="group relative px-8 py-3 cursor-pointer bg-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all flex items-center gap-2"
                @click="goToChangeFiles"
              >
                <Icon icon="mdi:file-replace-outline" class="w-5 h-5" />
                <span>New version</span>
              </button>
              <button
                v-if="file_data?.files_available !== false"
                class="group relative px-8 py-3 cursor-pointer bg-gradient-to-r from-mint-green-100 to-periwinkle-100 font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
                :disabled="downloading"
                @click="startDownload"
                :aria-busy="downloading ? 'true' : 'false'"
              >
                <span>{{ downloading ? "Downloading..." : "Download" }}</span>
              </button>
            </section>
          </div>
        </section>

        <!-- Footer -->
        <div class="mt-8 text-center">
          <p class="text-xs text-gray-500">
            Powered by VUO and Data@Elettra infrastructure © 2025
          </p>
        </div>
      </div>
    </div>

    <FilePreviewModal
      v-if="previewFile"
      :file="previewFile"
      :investigation-id="investigationId"
      :investigation-name="investigation_info?.name || ''"
      @close="previewFile = null"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "../services/api";
import authService from "@/services/authService";
import FileStructureComponent from "@/components/FileStructure/FileStructureComponent.vue";
import FilePreviewModal from "@/components/FilePreviewModal.vue";
import HeaderBar from "@/components/HeaderBar.vue";
import LoginDropdown from "@/components/LoginDropdown.vue";
import { useCitationComposer } from "@/composables/useCitation";
import { useLogger } from "@/stores/useLogger";
import { useInvestigationStore } from "@/stores/InvestigationStore";
import { useLoadingService } from "@/services/LoadingService"; // ✅ corrected path
import { Icon } from "@iconify/vue";

const loadingSvc = useLoadingService();
const taskId = "dataset-page";

const route = useRoute();
const router = useRouter();
const logger = useLogger();
const investigationStore = useInvestigationStore();
logger.wireLifecycle();

const investigationId = route.params.investigation_id;
const parsedVersion = Number.parseInt(route.query.version, 10);
// --- Reactive state ---
const logo = ref(null);
const previewFile = ref(null);
const investigation_info = ref(null); // Basic info from VUO API
const file_data = ref(null); // File structure, size, extensions

const loading = ref(false);
const error = ref(null);
const loading_ok = ref(false);
const downloading = ref(false);
// True when the logged-in user is among this investigation's uploaders
const isOwner = ref(false);

// Separate loading state for file structure
const file_structure_loading = ref(false);
const file_structure_error = ref(null);

// Dataset versions (ds0, ds1, ...); 0 is the original upload.
// null = not chosen yet: the backend resolves it to the latest version.
const selectedVersion = ref(
  Number.isInteger(parsedVersion) && parsedVersion >= 0 ? parsedVersion : null,
);
const versions = ref([]);
const latestVersion = computed(() =>
  versions.value.length ? Math.max(...versions.value) : null,
);
const isLatestVersion = computed(
  () =>
    selectedVersion.value !== null &&
    selectedVersion.value === latestVersion.value,
);

// Versions other than 0 use their .description file, falling back to the
// investigation's original description when the file is missing or empty.
const descriptionText = computed(
  () =>
    (selectedVersion.value && file_data.value?.description) ||
    investigation_info.value?.description ||
    "No description available.",
);

function changeVersion(version) {
  if (version === selectedVersion.value) return;
  selectedVersion.value = version;
  router.replace({
    query: { ...route.query, version },
  });
  loadFileStructure();
}

// VUO returns "doi", the public catalog "DOI"
const doi = computed(
  () => investigation_info.value?.doi || investigation_info.value?.DOI || "",
);

// --- Citation handling ---
const { citation, compose } = useCitationComposer();

function createCitation(citationType) {
  const piFullName =
    investigation_info.value?.principal_investigator?.trim() || "";

  // Split by first space only
  const [first, ...rest] = piFullName.split(/\s+/);
  const last = rest.join(" ");

  const principalInvestigator = {
    name: first || "",
    lastname: last || "",
  };

  const citationData = {
    title:
      investigation_info.value?.title || investigation_info.value?.name || "",
    date: investigation_info.value?.publication_date || "",
    upload_type: "",
    uploaders: [principalInvestigator],
    doi: doi.value,
    investigation_id: route.params.investigation_id,
    citationType,
  };

  try {
    compose(citationData);
  } catch (e) {
    logger.log("Failed to build citation", e);
  }
}

// --- File structure loading ---
async function loadFileStructure() {
  file_structure_loading.value = true;
  file_structure_error.value = null;
  file_data.value = null;

  try {
    const filesResponse = await apiClient.getInvestigationFiles(
      investigationId,
      investigation_info.value?.name || null,
      selectedVersion.value,
    );

    if (filesResponse.data.result === "OK") {
      console.log("File structure loaded:", filesResponse.data.message);
      file_data.value = filesResponse.data.message;
      versions.value = filesResponse.data.message.versions || [];
      selectedVersion.value = filesResponse.data.message.version ?? 0;
    } else {
      throw new Error(
        filesResponse.data.message?.error || "Failed to load file data",
      );
    }
  } catch (fileError) {
    file_structure_error.value =
      fileError?.message || "Failed to load file structure";
    logger.log("Failed to load file structure", fileError);
  } finally {
    file_structure_loading.value = false;
  }
}

// --- Ownership (enables "Change files") ---
// The logged-in user's own investigations, including private ones that are
// not in the public catalog. null when not logged in or the call fails.
async function fetchMyInvestigation() {
  if (!authService.currentUser.value) return null;
  try {
    const resp = await apiClient.getMyInvestigationList();
    if (resp.data.result !== "OK") return null;
    const mine = resp.data.message.investigations || [];
    return (
      mine.find((inv) => String(inv.id) === String(investigationId)) || null
    );
  } catch (e) {
    logger.log("Failed to load my investigations", e);
    return null;
  }
}

// Full investigation info from VUO, fetched with the logged-in user's session
async function fetchInvestigationInfo() {
  const resp = await apiClient.getInvestigationInfoByID(investigationId);
  if (resp.data.result !== "OK") {
    throw new Error(
      resp.data.message?.error?.details || "Failed to load investigation info",
    );
  }
  return resp.data.message;
}

function goToChangeFiles() {
  logger.log("button_clicked", { metadata: { button: "change_files" } });
  router.push({
    name: "AddDataset",
    params: { investigation_id: investigationId },
  });
}

// --- Lifecycle ---
onMounted(async () => {
  loadingSvc.start(taskId, { title: "Fetching data…" });
  logo.value = `${process.env.BASE_URL}images/enodo-logo.png`;
  loading.value = true;
  error.value = null;

  try {
    console.log("Attempting to load investigation with ID:", investigationId);

    // Own investigations may be private, so they are not in the public store
    const mine = await fetchMyInvestigation();
    isOwner.value = !!mine;

    const investigation =
      route.name === "myUploadDataset"
        ? { ...mine, ...(await fetchInvestigationInfo()) }
        :(await investigationStore.getByIdOrFetch(investigationId)) || mine;
    console.log("Investigation retrieved:", investigation);

    if (!investigation) {
      throw new Error(`Investigation with ID "${investigationId}" not found.`);
    }

    investigation_info.value = investigation;
    loading_ok.value = true;

    // Build the initial citation (APA)
    createCitation("apa");

    // Now load file structure separately (still needs API call)
    await loadFileStructure();
  } catch (e) {
    error.value = e?.message || "An unexpected error occurred";
    loading_ok.value = false;
  } finally {
    loading.value = false;
    loadingSvc.stop(taskId);
  }
});

// --- Download handler ---
const startDownload = () => {
  downloading.value = true;

  const downloadUrl = `/enodo/api/download_investigation/${investigationId}?investigation_name=${encodeURIComponent(
    investigation_info.value?.name || "",
  )}&version=${selectedVersion.value ?? 0}`;
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.setAttribute("download", "");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  downloading.value = false;
};

function extract_extensions(folder, extensions = new Set()) {
  if (
    folder.file_type !== "folder" &&
    folder.file_type !== "h5-folder" &&
    !folder.file_type.startsWith("dataset_")
  ) {
    extensions.add(folder.file_type);
  }
  if (folder.children && folder.children.length !== 0) {
    folder.children.forEach((child) => {
      extract_extensions(child, extensions);
    });
  }
  return extensions;
}

const fileTypeKeys = computed(() => {
  if (!file_data.value?.file_structure) return "—";

  const extensions = new Set();
  extract_extensions(file_data.value.file_structure, extensions);

  const keys = Array.from(extensions);
  if (keys.length === 0) return "—";
  if (keys.length === 1) return keys[0];
  if (keys.length === 2) return keys.join(" and ");
  return keys.slice(0, -1).join(", ") + " and " + keys[keys.length - 1];
});

async function copyText(text) {
  // Try modern API if supported & secure
  if (navigator?.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  // Fallback: hidden textarea + execCommand
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "-9999px";
  document.body.appendChild(ta);

  const selection = document.getSelection();
  const originalRange =
    selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  ta.select();
  ta.setSelectionRange(0, ta.value.length); // iOS/Safari

  try {
    document.execCommand("copy");
  } finally {
    document.body.removeChild(ta);
    // restore selection if we had one
    if (originalRange && selection) {
      selection.removeAllRanges();
      selection.addRange(originalRange);
    }
  }
}

const copyToClipboard = async () => {
  try {
    await copyText(citation.value);
    // copied.value = true;
    // setTimeout(() => (copied.value = false), 2000);
  } catch (err) {
    console.error("Failed to copy text:", err);
  }
};
</script>
